import { useState, useEffect, useCallback, useRef } from 'react';
import { notify } from "@/shared/utils/notify";
import { messagesService } from '../../../shared/services';

// Solo mostrar mensajes de los últimos 2 días
const MESSAGE_RETENTION_DAYS = 2;
const getTwoDaysAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - MESSAGE_RETENTION_DAYS);
  return date;
};

export interface IOrder {
  id: number;
  order_status_id: number;
  total_price: number;
  createdAt: string;
  confirmation_code?: string;
  order_details?: {
    id: number;
    product_name: string;
    quantity: number;
    total_price_tax_incl: number;
  }[];
}

export interface IMessage {
  id?: number;
  user_id: number;
  admin_id?: number;
  order_id?: number;
  message: string;
  is_admin?: boolean;
  read_at?: string;
  createdAt?: string;
  updatedAt?: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  admin?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  order?: IOrder;
}

export interface IMessageResponse extends Omit<IMessage, 'id'> {
  id: number;
}

export const useMessages = (selectedUserId?: number) => {
  const [messages, setMessages] = useState<IMessageResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const orderIdRef = useRef<number | undefined>(undefined);

  // Cargar mensajes
  const loadMessages = useCallback(async () => {
    if (!selectedUserId) return;
    
    try {
      const twoDaysAgo = getTwoDaysAgo();
      // Construir query: buscar por user_id y order_id si está disponible
      const query: any = {
        user_id: selectedUserId,
        $sort: { createdAt: 1 },
        createdAt: { $gte: twoDaysAgo.toISOString() },
      };
      
      // Si ya tenemos un order_id de mensajes anteriores, usarlo también
      if (orderIdRef.current) {
        query.order_id = orderIdRef.current;
      }
      
      const response = await messagesService.find({ query });
      const loadedMessages = response.data || [];
      
      // Guardar order_id si está disponible en los mensajes
      if (loadedMessages.length > 0 && !orderIdRef.current) {
        const msgWithOrder = loadedMessages.find(m => m.order_id);
        if (msgWithOrder?.order_id) {
          orderIdRef.current = msgWithOrder.order_id;
        }
      }
      
      setMessages(loadedMessages);
    } catch (err: any) {
      // Solo mostrar error si no es un 404 (servicio no disponible)
      if (err.code !== 404) {
        console.error('Error loading messages:', err);
      }
      // Si es 404, simplemente mantener mensajes vacíos
      if (err.code === 404) {
        setMessages([]);
      }
    }
  }, [selectedUserId]);

  // Cargar mensajes iniciales con loading
  const loadMessagesInitial = useCallback(async () => {
    if (!selectedUserId) return;
    
    setIsLoading(true);
    try {
      const twoDaysAgo = getTwoDaysAgo();
      const query: any = {
        user_id: selectedUserId,
        $sort: { createdAt: 1 },
        createdAt: { $gte: twoDaysAgo.toISOString() },
      };
      
      const response = await messagesService.find({ query });
      const loadedMessages = response.data || [];
      
      // Guardar order_id si está disponible
      if (loadedMessages.length > 0) {
        const msgWithOrder = loadedMessages.find(m => m.order_id);
        if (msgWithOrder?.order_id) {
          orderIdRef.current = msgWithOrder.order_id;
        }
      }
      
      setMessages(loadedMessages);
    } catch (err: any) {
      // Solo mostrar error si no es un 404 (servicio no disponible)
      if (err.code !== 404) {
        console.error('Error loading messages:', err);
        notify.error('Error al cargar los mensajes');
      } else {
        // Servicio no disponible, mantener mensajes vacíos
        setMessages([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedUserId]);

  // Enviar mensaje
  const sendMessage = async (text: string, isAdmin: boolean = false) => {
    if (!selectedUserId || !text.trim()) return;
    
    setIsSending(true);
    try {
      const messageData: any = {
        user_id: selectedUserId,
        message: text.trim(),
        is_admin: isAdmin,
      };
      
      // Incluir order_id si está disponible
      if (orderIdRef.current) {
        messageData.order_id = orderIdRef.current;
      }
      
      const newMessage = await messagesService.create(messageData);
      
      // Guardar order_id si viene en la respuesta
      if (newMessage.order_id && !orderIdRef.current) {
        orderIdRef.current = newMessage.order_id;
      }
      
      // Agregar el mensaje a la lista inmediatamente
      setMessages(prev => {
        if (prev.find(m => m.id === newMessage.id)) {
          return prev;
        }
        return [...prev, newMessage].sort((a, b) => 
          new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime()
        );
      });
      
      // Recargar mensajes después de un breve delay para asegurar sincronización
      setTimeout(() => {
        loadMessages();
      }, 500);
    } catch (err: any) {
      console.error('Error sending message:', err);
      notify.error('Error al enviar el mensaje');
    } finally {
      setIsSending(false);
    }
  };

  // Polling para nuevos mensajes cada 2 segundos (más tiempo real)
  useEffect(() => {
    if (!selectedUserId) return;

    // Cargar mensajes iniciales
    loadMessagesInitial();

    // Iniciar polling más frecuente
    pollingRef.current = setInterval(() => {
      loadMessages();
    }, 2000);

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [selectedUserId, loadMessages, loadMessagesInitial]);

  return {
    messages,
    isLoading,
    isSending,
    sendMessage,
    loadMessages,
  };
};
